'use client';

import React, { useEffect, useRef, useState } from 'react';

interface Stat {
  value: string;
  label: string;
  sub: string;
  color: string;
}

const stats: Stat[] = [
  { value: '2,400+', label: 'Active landlords', sub: 'across 38 states', color: '#A78BFA' },
  { value: '187k', label: 'Units managed', sub: 'and growing daily', color: '#EEEEF0' },
  { value: 'R20.1B', label: 'Rent collected', sub: 'through the platform', color: '#A78BFA' },
  { value: '94%', label: 'Avg collection rate', sub: 'vs 81% industry avg', color: '#22C55E' },
];

export default function StatsBar() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-12 sm:py-20 px-4 sm:px-6 relative overflow-hidden border-y border-[rgba(124,58,237,0.1)]">
      {/* Background beam SVG */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none opacity-30"
        preserveAspectRatio="none"
        viewBox="0 0 1200 120"
        fill="none"
      >
        <defs>
          <linearGradient id="stat-beam" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#7C3AED" stopOpacity="0" />
            <stop offset="40%" stopColor="#A78BFA" />
            <stop offset="60%" stopColor="#7C3AED" />
            <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0 60 Q 300 20, 600 60 T 1200 60"
          stroke="url(#stat-beam)"
          strokeWidth="1.5"
          className="animate-beam"
        />
      </svg>

      <div className="max-w-6xl mx-auto">
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 stagger-children ${visible ? 'is-visible' : ''}`}>
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`reveal-blur flex flex-col items-center text-center lg:border-r lg:last:border-r-0 border-[rgba(255,255,255,0.05)] px-3 sm:px-6 py-3 sm:py-4 ${visible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <span
                className="text-3xl sm:text-5xl lg:text-6xl font-manrope font-extrabold tracking-tighter mb-2"
                style={{ color: stat.color }}
              >
                {stat.value}
              </span>
              <span className="text-sm font-manrope font-semibold text-[#EEEEF0] mb-1">{stat.label}</span>
              <span className="text-xs font-mono text-[#5A5A6E]">{stat.sub}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}